"use client"

import {
  authIdentity,
  prepareBroadcastRequest,
} from "@defuse-protocol/internal-utils"
import { createVerificationMessage } from "@src/components/DefuseSDK/core/messages"
import {
  authenticatePrivateIntents,
  isPrivateIntentsAuthenticated,
} from "@src/components/DefuseSDK/features/machines/privateIntents"
import { extractWalletErrorCode } from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import { teardownPrivateMode } from "@src/hooks/teardownPrivateMode"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { useWalletAgnosticSignMessage } from "@src/hooks/useWalletAgnosticSignMessage"
import { usePrivateModeStore } from "@src/stores/usePrivateModeStore"
import { chainTypeToAuthMethod } from "@src/utils/chainTypeToAuthMethod"
import { WalletConnectionFailure } from "@src/utils/classifyWalletConnectionFailure"
import { logger } from "@src/utils/logger"
import { trackEvent } from "@src/utils/trackEvent"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export function usePrivateModeAuth() {
  const { state } = useConnectWallet()
  const signMessage = useWalletAgnosticSignMessage()
  const {
    isPrivateModeEnabled,
    setPrivateModeEnabled,
    authenticatedIntentsUserId,
  } = usePrivateModeStore()
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  const isConnected = state.address != null
  const authMethod = state.chainType
    ? chainTypeToAuthMethod(state.chainType)
    : null

  const intentsUserId = useMemo(
    () =>
      state.address && authMethod
        ? authIdentity.authHandleToIntentsUserId(state.address, authMethod)
        : null,
    [state.address, authMethod]
  )

  useEffect(() => {
    if (!isPrivateModeEnabled || isAuthenticating) return

    async function syncAuthStatus() {
      const isAuthenticated = await isPrivateIntentsAuthenticated()
      if (!isAuthenticated) {
        usePrivateModeStore.setState({
          isPrivateModeEnabled: false,
          authenticatedIntentsUserId: null,
        })
      }
    }

    void syncAuthStatus()
    const interval = setInterval(syncAuthStatus, 30_000)
    return () => clearInterval(interval)
  }, [isPrivateModeEnabled, isAuthenticating])

  const prevAddressRef = useRef<string | undefined>(state.address)

  useEffect(() => {
    if (!isPrivateModeEnabled) {
      prevAddressRef.current = state.address
      return
    }

    // Wallet still loading after page reload — wait for reconnection
    if (state.address == null && prevAddressRef.current == null) {
      return
    }

    // Explicit disconnect: address went from defined to undefined
    if (state.address == null && prevAddressRef.current != null) {
      teardownPrivateMode()
      prevAddressRef.current = state.address
      return
    }

    // Connected wallet doesn't match the authenticated session
    if (
      authenticatedIntentsUserId != null &&
      intentsUserId !== authenticatedIntentsUserId
    ) {
      teardownPrivateMode()
    }

    prevAddressRef.current = state.address
  }, [
    state.address,
    intentsUserId,
    isPrivateModeEnabled,
    authenticatedIntentsUserId,
  ])

  const authenticate = useCallback(async () => {
    if (!state.address || !state.chainType || !authMethod || !intentsUserId) {
      setAuthError("Please connect your wallet first")
      return false
    }

    setIsAuthenticating(true)
    setAuthError(null)

    try {
      const walletMessage = await createVerificationMessage(
        { signerId: intentsUserId },
        state.chainType
      )
      const signatureResult = await signMessage(walletMessage)

      const signedData = prepareBroadcastRequest.prepareSwapSignedData(
        signatureResult,
        { userAddress: state.address, userChainType: authMethod }
      )

      const authResult = await authenticatePrivateIntents({
        signedData,
        address: state.address,
        authMethod,
      })

      if ("err" in authResult) {
        setAuthError(authResult.err)
        return false
      }

      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      const failure = new WalletConnectionFailure(
        error,
        extractWalletErrorCode(error, null)
      )
      setAuthError(failure.failureType === "rejected" ? null : message)
      trackEvent("private_mode_auth_failed", {
        chain: state.chainType ?? "unknown",
        failure_type: failure.failureType,
      })
      if (failure.isActionable) {
        logger.error("Private mode auth failed", {
          error: failure,
          chain: state.chainType ?? "unknown",
        })
      }
      return false
    } finally {
      setIsAuthenticating(false)
    }
  }, [authMethod, intentsUserId, signMessage, state.address, state.chainType])

  const ensureAuthenticated = useCallback(async () => {
    const alreadyAuthenticated = await isPrivateIntentsAuthenticated().catch(
      () => false
    )
    if (alreadyAuthenticated) {
      if (
        authenticatedIntentsUserId !== intentsUserId &&
        intentsUserId != null
      ) {
        usePrivateModeStore.setState({
          authenticatedIntentsUserId: intentsUserId,
        })
      }
      return true
    }
    return authenticate()
  }, [authenticate, authenticatedIntentsUserId, intentsUserId])

  const enablePrivateMode = useCallback(async () => {
    const success = await ensureAuthenticated()
    if (success) {
      usePrivateModeStore.setState({
        isPrivateModeEnabled: true,
        authenticatedIntentsUserId: intentsUserId,
      })
    }
    return success
  }, [ensureAuthenticated, intentsUserId])

  const disablePrivateMode = useCallback(() => {
    setPrivateModeEnabled(false)
    setAuthError(null)
  }, [setPrivateModeEnabled])

  const togglePrivateMode = useCallback(async () => {
    if (isPrivateModeEnabled) {
      await disablePrivateMode()
      return
    }

    await enablePrivateMode()
  }, [disablePrivateMode, enablePrivateMode, isPrivateModeEnabled])

  return {
    isPrivateModeEnabled,
    isAuthenticating,
    authError,
    isConnected,
    authenticate,
    ensureAuthenticated,
    enablePrivateMode,
    disablePrivateMode,
    togglePrivateMode,
  }
}
